import {
  BackupStatus,
  ConditionOperatorEnum,
  ConfigurationStatus,
  type Condition,
} from '@repo/api-gw-sdk';

import {
  createBackupStatusInCondition,
  createBackupStatusNotInCondition,
} from './createBackupStatusNotInCondition';

import {
  BACKUP_STATUS_PROPERTY,
  CONFIGURATION_STATUS_PROPERTY,
} from '../queryBuilder/propertyNames';

const {
  CONFIGURATION_STATUS_ASSIGNED: ASSIGNED,
  CONFIGURATION_STATUS_UNASSIGNED: UNASSIGNED,
  CONFIGURATION_STATUS_TERMINATED: TERMINATED,
  CONFIGURATION_STATUS_DISCONNECTED: DISCONNECTED,
  CONFIGURATION_STATUS_ACCESS_LOST: ACCESS_LOST,
  CONFIGURATION_STATUS_UNSUPPORTED: UNSUPPORTED,
  CONFIGURATION_STATUS_CLASSIFICATION_FAILED: CLASSIFICATION_FAILED,
  CONFIGURATION_STATUS_EXCLUDED_FROM_BACKUP: EXCLUDED_FROM_BACKUP,
  CONFIGURATION_STATUS_LIMIT_EXCEEDED: LIMIT_EXCEEDED,
  CONFIGURATION_STATUS_INITIAL_CLASSIFICATION: INITIAL_CLASSIFICATION,
} = ConfigurationStatus;

/**
 * Where each backup status lands on the configuration axis. Eight map by name; the rest
 * collapse into whether a backup is configured, so ACTION_REQUIRED (a pending action over
 * either answer) is both.
 *
 * Known, accepted divergences from the backup status it replaces:
 * - a removed account reads DISCONNECTED here and TERMINATED there;
 * - a resource with several out-of-scope causes reads the cause the configuration order
 *   ranks first (excluded and over-limit above unsupported, unsupported above access lost).
 */
export const CONFIGURATION_STATUSES_OF_BACKUP_STATUS: Record<
  BackupStatus,
  ConfigurationStatus[]
> = {
  [BackupStatus.Terminated]: [
    ConfigurationStatus.CONFIGURATION_STATUS_TERMINATED,
  ],
  [BackupStatus.Disconnected]: [
    ConfigurationStatus.CONFIGURATION_STATUS_DISCONNECTED,
  ],
  [BackupStatus.AccessLost]: [
    ConfigurationStatus.CONFIGURATION_STATUS_ACCESS_LOST,
  ],
  [BackupStatus.Unsupported]: [
    ConfigurationStatus.CONFIGURATION_STATUS_UNSUPPORTED,
  ],
  [BackupStatus.ClassificationFailed]: [
    ConfigurationStatus.CONFIGURATION_STATUS_CLASSIFICATION_FAILED,
  ],
  [BackupStatus.ExcludedFromBackup]: [
    ConfigurationStatus.CONFIGURATION_STATUS_EXCLUDED_FROM_BACKUP,
  ],
  [BackupStatus.LimitExceeded]: [
    ConfigurationStatus.CONFIGURATION_STATUS_LIMIT_EXCEEDED,
  ],
  [BackupStatus.InitialClassification]: [
    ConfigurationStatus.CONFIGURATION_STATUS_INITIAL_CLASSIFICATION,
  ],
  [BackupStatus.Protected]: [ASSIGNED],
  [BackupStatus.ViolationsDetected]: [ASSIGNED],
  [BackupStatus.AllViolationsMuted]: [ASSIGNED],
  [BackupStatus.Dlsg]: [ASSIGNED],
  [BackupStatus.NotBackedUp]: [UNASSIGNED],
  [BackupStatus.GenericBackups]: [UNASSIGNED],
  [BackupStatus.ActionRequired]: [ASSIGNED, UNASSIGNED],
  [BackupStatus.BackupPostureStatusUnspecified]: [],
};

/** The union of the configuration statuses of `statuses`, without repeats. */
const toConfigurationStatuses = (statuses: BackupStatus[]) => [
  ...new Set(
    statuses.flatMap(
      (status) => CONFIGURATION_STATUSES_OF_BACKUP_STATUS[status]
    )
  ),
];

/**
 * The status the inventory's Unsupported toggle holds out of view, and the property it is
 * written on: the backup status, or with `InventoryStatusColumns` on, the configuration status.
 */
export const unsupportedStatusOf = (
  useConfigurationStatus: boolean
): { property: string; status: string } =>
  useConfigurationStatus
    ? {
        property: CONFIGURATION_STATUS_PROPERTY,
        status: ConfigurationStatus.CONFIGURATION_STATUS_UNSUPPORTED,
      }
    : { property: BACKUP_STATUS_PROPERTY, status: BackupStatus.Unsupported };

/**
 * Which configuration statuses each default view shows. A record over the whole enum, so a new
 * status fails the typecheck until its visibility is decided in every view.
 */
export const SHOWN_CONFIGURATION_STATUSES: Record<
  string,
  Record<ConfigurationStatus, boolean>
> = {
  // The inventory opens without the statuses the customer or the product took out of scope.
  // Terminated stays in: the server already drops terminated resources without Eon snapshots,
  // so the ones left are restorable and belong in the default view.
  inventory: {
    [ASSIGNED]: true,
    [UNASSIGNED]: true,
    [INITIAL_CLASSIFICATION]: true,
    [ACCESS_LOST]: true,
    [CLASSIFICATION_FAILED]: true,
    [TERMINATED]: true,
    [DISCONNECTED]: false,
    [EXCLUDED_FROM_BACKUP]: false,
    [LIMIT_EXCEEDED]: false,
    [UNSUPPORTED]: false,
  },
  // The dashboard counts only resources with a bucket to be counted into.
  dashboard: {
    [ASSIGNED]: true,
    [UNASSIGNED]: true,
    [INITIAL_CLASSIFICATION]: true,
    [ACCESS_LOST]: false,
    [CLASSIFICATION_FAILED]: false,
    [TERMINATED]: true,
    [DISCONNECTED]: false,
    [EXCLUDED_FROM_BACKUP]: false,
    [LIMIT_EXCEEDED]: false,
    [UNSUPPORTED]: false,
  },
  // A backup policy's resource count leaves out everything the policy cannot back up.
  backupPolicyResources: {
    [ASSIGNED]: true,
    [UNASSIGNED]: true,
    [INITIAL_CLASSIFICATION]: true,
    [ACCESS_LOST]: false,
    [CLASSIFICATION_FAILED]: false,
    [TERMINATED]: true,
    [DISCONNECTED]: false,
    [EXCLUDED_FROM_BACKUP]: false,
    [LIMIT_EXCEEDED]: false,
    [UNSUPPORTED]: false,
  },
  // A cluster's node list drops only nodes that are gone or out of reach.
  clusterNodes: {
    [ASSIGNED]: true,
    [UNASSIGNED]: true,
    [INITIAL_CLASSIFICATION]: true,
    [ACCESS_LOST]: false,
    [CLASSIFICATION_FAILED]: true,
    [TERMINATED]: false,
    [DISCONNECTED]: false,
    [EXCLUDED_FROM_BACKUP]: true,
    [LIMIT_EXCEEDED]: true,
    [UNSUPPORTED]: true,
  },
};

export type ScopeView = keyof typeof SHOWN_CONFIGURATION_STATUSES;

/**
 * A default view's scope. With `useConfigurationStatus` on it lists the configuration statuses
 * the view shows, plus those of `revealed` for a link that opens a slice the view would hide.
 * Off, it excludes `hiddenBackupStatuses` from the backup status, as it always did.
 */
export const createScopeCondition = (
  view: ScopeView,
  hiddenBackupStatuses: BackupStatus[],
  useConfigurationStatus: boolean,
  revealed: BackupStatus[] = []
): Condition => {
  if (!useConfigurationStatus) {
    return createBackupStatusNotInCondition(hiddenBackupStatuses);
  }

  const shown = SHOWN_CONFIGURATION_STATUSES[view];

  return {
    property: CONFIGURATION_STATUS_PROPERTY,
    operator: ConditionOperatorEnum.In,
    value: [
      ...new Set([
        ...Object.values(ConfigurationStatus).filter((status) => shown[status]),
        ...toConfigurationStatuses(revealed),
      ]),
    ],
  };
};

/** An "is one of" scope condition; flag on, it selects the union of the configuration statuses. */
export const createIncludedStatusCondition = (
  backupStatuses: BackupStatus[],
  useConfigurationStatus: boolean
): Condition =>
  useConfigurationStatus
    ? {
        property: CONFIGURATION_STATUS_PROPERTY,
        operator: ConditionOperatorEnum.In,
        value: toConfigurationStatuses(backupStatuses),
      }
    : createBackupStatusInCondition(backupStatuses);
